import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Stack,
  InputRightElement,
  InputGroup,
  IconButton,
  Text,
  Center,
  Image,
  VStack,
  useToast,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Link as ChakraLink } from "@chakra-ui/react";
import { AUTH_PAGES, PASSWORD_TYPE, PROVIDER_PAGES } from "../../../base/router/pages";
import logo from "../../../assets/images/logo2.png";
import { useValidPasswordAdvance } from "../../../hooks/useValidPasswordAdvance";
import PassConstraint from "../../../components/PassConstraint";
import {
  useGetAuthUser,
  useSetAccessTokenFromAuthUser,
  useUserRegistration,
} from "../../../api/AuthApi";
import React from "react";
import PageContainer from "../../../components/PageContainer";

export const CheckPasswordPage = (): JSX.Element => {
  const { passwordAdvance, setPasswordAdvance, passwordAdvanceIsValid } =
    useValidPasswordAdvance(null);
  const [confirmPass, setConfirmPass] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");
  const [showPassword, setshowPassword] = useState<boolean>(false);
  const [showCPassword, setshowCPassword] = useState<boolean>(false);
  const { data: authUser } = useGetAuthUser();
  const { type }: any = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  const onComparePassword = React.useCallback(
    (confirmPass: string) => {
      setConfirmPass(confirmPass);
      if (confirmPass === "") {
        setConfirmPassError("");
        return;
      }
      if (confirmPass !== passwordAdvance) {
        setConfirmPassError("The passwords you have entered don't match");
      } else {
        setConfirmPassError("");
      }
    },
    [passwordAdvance],
  );

  useEffect(() => {
    onComparePassword(confirmPass);
  }, [confirmPass, onComparePassword]);

  useEffect(() => {
    switch (type) {
      case PASSWORD_TYPE.invite:
        if (!authUser?.email) navigate(AUTH_PAGES.login);
        break;
      case PASSWORD_TYPE.signup:
        if (!authUser?.firstName || !authUser?.lastName || !authUser?.email)
          navigate(AUTH_PAGES.register);
        break;
    }
  }, [authUser, navigate, type]);

  const { mutateAsync: register, isPending: isRegistering } = useUserRegistration();
  const { mutateAsync: setAccessTokenFromAuthUser } = useSetAccessTokenFromAuthUser();

  const onNextPressed = async () => {
    if (type === PASSWORD_TYPE.invite && authUser?.email && passwordAdvance) {
      // TODO: send invite
    } else {
      if (
        authUser?.firstName &&
        authUser?.lastName &&
        authUser?.email &&
        authUser?.providerId &&
        authUser?.phone &&
        passwordAdvance
      ) {
        try {
          await register({
            firstName: authUser?.firstName,
            lastName: authUser?.lastName,
            email: authUser?.email,
            providerId: authUser?.providerId,
            phone: authUser?.phone,
            password: passwordAdvance,
          });
          await setAccessTokenFromAuthUser();
          navigate(PROVIDER_PAGES.home);
        } catch (error) {
          console.log(error);
        }
      } else {
        toast({ description: "An unexpected error occured", status: "error" });
      }
    }
  };

  const handleClickShowPassword = () => {
    setshowPassword(!showPassword);
  };

  const handleClickShowCPassword = () => {
    setshowCPassword(!showCPassword);
  };

  const isButtonDisabled = () => {
    return (
      passwordAdvanceIsValid[0].text !== "Password Strength: Good" ||
      confirmPassError !== "" ||
      isRegistering
    );
  };

  return (
    <PageContainer variant="centered-auth">
      <VStack minH="100%">
        <Image src={logo} alt="Topflight Apps" width="160px" />
        <Card width={488} marginTop={30}>
          <CardBody paddingLeft={"60px"} paddingRight={"60px"} borderRadius={"20px"}>
            <Text fontSize="md" align="center" mt={2} color="gray">
              Create a password
            </Text>
            <form>
              <Stack spacing={4} mt={8}>
                <FormControl variant="floating">
                  <InputGroup>
                    <Input
                      placeholder="" // leave empty for floating label
                      aria-label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={passwordAdvance ?? ""}
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      onChange={(e: any) => setPasswordAdvance(e.target.value as string)}
                      disabled={isRegistering}
                    />
                    <FormLabel>Password</FormLabel>
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        color="gray.500"
                        aria-label="Show password"
                        size="lg"
                        style={{ top: "8px", right: "8px" }}
                        icon={!showPassword ? <BsEye /> : <BsEyeSlash />}
                        onClick={handleClickShowPassword}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl variant="floating" isInvalid={Boolean(confirmPassError)}>
                  <InputGroup>
                    <Input
                      placeholder="" // leave empty for floating label
                      aria-label="Confirm Password"
                      name="confirmpassword"
                      type={showCPassword ? "text" : "password"}
                      required
                      value={confirmPass}
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      onChange={(e: any) => setConfirmPass(e?.target?.value as string)}
                      disabled={isRegistering}
                    />
                    <FormLabel>Confirm Password</FormLabel>
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        color="gray.500"
                        aria-label="Show password"
                        size="lg"
                        style={{ top: "8px", right: "8px" }}
                        icon={!showCPassword ? <BsEye /> : <BsEyeSlash />}
                        onClick={handleClickShowCPassword}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{confirmPassError}</FormErrorMessage>
                </FormControl>
                <PassConstraint passwordAdvanceIsValid={passwordAdvanceIsValid} />
                <Button
                  my={4}
                  backgroundColor={"brandYellow.500"}
                  textColor={"brandBlue.800"}
                  isLoading={isRegistering}
                  isDisabled={isButtonDisabled()}
                  onClick={() => void onNextPressed()}
                >
                  Continue
                </Button>
                <Center flexWrap="wrap">
                  <Text fontSize="sm" color="gray.500" me={1}>
                    Have an account?
                  </Text>
                  <ChakraLink
                    as={Link}
                    to={AUTH_PAGES.login}
                    color="gray.600"
                    fontWeight="medium"
                    fontSize="sm"
                    _hover={{ color: "gray.900" }}
                  >
                    Login
                  </ChakraLink>
                </Center>
              </Stack>
            </form>
          </CardBody>
        </Card>
      </VStack>
    </PageContainer>
  );
};
